import { useState, useEffect } from 'react'
import ManagerMobileLayout from '../../components/ManagerMobileLayout'
import api from '../../lib/api'

interface Timesheet {
  id: string
  weekStart: string
  weekEnd: string
  status: string
  totalHours: number
  overtimeHours: number
  employee: { firstName: string; lastName: string }
}

const STATUS_COLORS: Record<string, string> = {
  draft:     'bg-gray-100 text-gray-600',
  validated: 'bg-blue-100 text-blue-700',
  signed:    'bg-green-100 text-green-700'
}
const STATUS_LABELS: Record<string, string> = { draft: 'Brouillon', validated: 'Validé', signed: 'Signé' }

const MONTHS = ['jan','fév','mar','avr','mai','juin','juil','août','sep','oct','nov','déc']
function fmtWeek(s: string, e: string) {
  const sd = new Date(s), ed = new Date(e)
  return `${sd.getDate()} ${MONTHS[sd.getMonth()]} – ${ed.getDate()} ${MONTHS[ed.getMonth()]}`
}

export default function ManagerTimesheets() {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([])
  const [filter, setFilter] = useState<'draft' | 'all'>('draft')
  const [loading, setLoading] = useState(true)

  useEffect(() => { load() }, [filter])

  async function load() {
    setLoading(true)
    const q = filter === 'draft' ? '?status=draft' : ''
    const r = await api.get(`/timesheets${q}`)
    setTimesheets(r.data)
    setLoading(false)
  }

  async function validate(id: string) {
    await api.put(`/timesheets/${id}/validate`)
    await load()
  }

  return (
    <ManagerMobileLayout>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-800">Feuilles d'heures</h1>
          <div className="flex bg-gray-100 rounded-lg p-1 text-xs">
            <button onClick={() => setFilter('draft')}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${filter === 'draft' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
              À valider
            </button>
            <button onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${filter === 'all' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
              Toutes
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : timesheets.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm text-gray-400">
            {filter === 'draft' ? 'Aucune feuille à valider' : 'Aucune feuille'}
          </div>
        ) : (
          <div className="space-y-2">
            {timesheets.map(ts => (
              <div key={ts.id} className="bg-white rounded-2xl shadow-sm p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-semibold text-gray-800">{ts.employee.firstName} {ts.employee.lastName !== '-' ? ts.employee.lastName : ''}</div>
                    <div className="text-sm text-gray-500 mt-0.5">{fmtWeek(ts.weekStart, ts.weekEnd)}</div>
                    <div className="text-sm font-medium text-blue-600 mt-0.5">
                      {ts.totalHours.toFixed(1)}h
                      {ts.overtimeHours > 0 && <span className="text-orange-500 ml-2">+{ts.overtimeHours.toFixed(1)}h sup.</span>}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[ts.status]}`}>
                    {STATUS_LABELS[ts.status]}
                  </span>
                </div>
                {ts.status === 'draft' && (
                  <button onClick={() => validate(ts.id)}
                    className="w-full mt-2 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-colors">
                    Valider la feuille
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </ManagerMobileLayout>
  )
}
